import {
  ACCOUNT_MAX_SUB_INDEX,
  Account,
  Amount,
  AppError,
  CommonErrorsKey,
  Fraction,
  ONE,
  Percent,
  Token,
  USD_TOKEN,
  formatUSD,
  isAddress,
} from "@eulerxyz/core-sdk"
import { createSelector } from "@reduxjs/toolkit"
import type { UseQueryResult } from "@tanstack/react-query"
import { type Address, getAddress, isAddressEqual, zeroAddress } from "viem"
import { selectUnitOfAccount } from "../datasets"
import type {
  GetAccountInfoData,
  GetManagedVaultData,
  GetVaultData,
} from "../query"
import {
  MerklReward,
  VaultResults,
  calculateTotalBorrowRewardApy,
  calculateTotalRewardApy,
  selectQueryStatus,
} from "../utils"
import {
  selectConvertUnitOfAccountAmountToPrice,
  selectPrice,
  selectPriceFromPriceApi,
} from "./pricing"
import { AccountVaults } from "./transactions"
import {
  NativeApy,
  selectAsset,
  selectManagedAsset,
  selectVaultsMap,
} from "./vaults"

// Sort by riskAdjustedValue (descending) and then by isCollateral
const sortByRiskAdjustedValue = (
  a: VaultAccountInfoWithCollateralValue,
  b: VaultAccountInfoWithCollateralValue,
) => {
  const aValue = a.riskAdjustedValue || 0n
  const bValue = b.riskAdjustedValue || 0n

  if (aValue !== bValue) {
    return Number(bValue - aValue)
  }

  if (aValue === 0n && bValue === 0n) {
    return Number(b.isCollateral) - Number(a.isCollateral)
  }

  return 0
}

export const selectAccountBalance = createSelector(
  [
    (accountInfo: GetAccountInfoData) =>
      accountInfo?.vaultAccountInfo.assetsAccount,
    (_, vault: GetVaultData) => selectAsset(vault),
  ],
  (assets, asset) => ({
    balance: new Fraction(assets || 0n).divide(10 ** (asset?.decimals || 18)),
    asset,
  }),
)

export const selectAssetBalance = createSelector(
  [
    (assetsAccount?: bigint) => assetsAccount,
    (_, vault: GetVaultData) => selectAsset(vault),
  ],
  (assetsAccount, asset) => ({
    balance: new Fraction(assetsAccount || 0n).divide(
      10 ** (asset?.decimals || 18),
    ),
    asset,
  }),
)

export const selectManagedAssetBalance = createSelector(
  [
    (assetsAccount?: bigint) => assetsAccount,
    (_, vault: GetManagedVaultData) => selectManagedAsset(vault),
  ],
  (assetsAccount, asset) => ({
    balance: new Fraction(assetsAccount || 0n).divide(
      10 ** (asset?.decimals || 18),
    ),
    asset,
  }),
)

export const selectTtlFromVaultAccountInfo = createSelector(
  [(vault?: NonNullable<GetAccountInfoData>["vaultAccountInfo"]) => vault],
  (vault) => {
    if (
      vault?.isController &&
      vault.liquidityInfo.liabilityValueBorrowing > 0n &&
      vault.liquidityInfo.timeToLiquidation > 0n
    ) {
      return vault.liquidityInfo.timeToLiquidation
    }
  },
)

export const selectTTL = createSelector(
  [(accountInfo: GetAccountInfoData) => accountInfo?.vaultAccountInfo],
  selectTtlFromVaultAccountInfo,
)

export const selectHealthFromVaultAccountInfo = createSelector(
  [(vault?: NonNullable<GetAccountInfoData>["vaultAccountInfo"]) => vault],
  (vault) => {
    if (
      vault?.isController &&
      vault.liquidityInfo.liabilityValueBorrowing > 0n &&
      vault.liquidityInfo.collateralValueLiquidation > 0n
    ) {
      return new Fraction(
        vault.liquidityInfo.collateralValueLiquidation,
      ).divide(vault.liquidityInfo.liabilityValueBorrowing)
    }
  },
)

export const selectHealthScore = createSelector(
  [(accountInfo: GetAccountInfoData) => accountInfo?.vaultAccountInfo],
  selectHealthFromVaultAccountInfo,
)

type VaultAccountInfo = NonNullable<GetAccountInfoData>["vaultAccountInfo"]

export type VaultAccountInfoWithCollateralValue = VaultAccountInfo & {
  riskAdjustedValue?: bigint
}

export type AccountInfos = {
  borrowVault?: VaultAccountInfo
  suppliedVaults: VaultAccountInfoWithCollateralValue[]
  collateralVaults?: VaultAccountInfo[]
  acceptedCollateralVaults: VaultAccountInfoWithCollateralValue[]
  rewards: {
    vault: Address
    reward: Address
    account: Address
    earned: bigint
  }[]
  account: Address
  assets?: Map<Address, { vault: Address; assetsAccount: bigint }>
  tokens: Address[]
}

export const selectNetAssetValueV2 = createSelector(
  [
    (results: UseQueryResult<GetVaultData>[]) => results,
    selectQueryStatus,
    (_, infos?: AccountInfos) => infos,
    (_, __, tokenPrices: Map<Address, bigint>) => tokenPrices,
    (_, __, ___, unitOfAccountPrices: Map<Address, bigint>) =>
      unitOfAccountPrices,
    (_, __, ___, ____, useOnlyCollateralVaults = true) =>
      useOnlyCollateralVaults,
  ],
  (
    results,
    status,
    infos,
    tokenPrices,
    unitOfAccountPrices,
    useOnlyCollateralVaults,
  ) => {
    if (!infos) return { ...status, data: undefined }
    const vaults = new Map<Address, GetVaultData>()
    let suppliedValue = Amount.fromRawAmount(USD_TOKEN, 0)
    let borrowedValue = Amount.fromRawAmount(USD_TOKEN, 0)
    let assetsValue = Amount.fromRawAmount(USD_TOKEN, 0)
    for (const result of results) {
      if (!result.data || !isAddress(result.data.vault)) continue
      vaults.set(getAddress(result.data.vault), result.data)
    }

    const borrowVault = vaults.get(
      getAddress(infos?.borrowVault?.vault || zeroAddress),
    )

    if (borrowVault) {
      const pricePerToken = tokenPrices.get(getAddress(borrowVault.asset)) || 0n
      if (pricePerToken) {
        const price = selectPriceFromPriceApi(
          infos?.borrowVault?.borrowed || 0n,
          borrowVault.assetDecimals,
          pricePerToken,
        )

        if (price) {
          borrowedValue = price.add(borrowedValue)
        }
      } else {
        const { address: borrowVaultUnitOfAccount } =
          selectUnitOfAccount(borrowVault)

        const price = selectPrice(
          infos?.borrowVault?.borrowed || 0n,
          borrowVault,
          unitOfAccountPrices.get(getAddress(borrowVaultUnitOfAccount)) || 0n,
        )

        if (price) {
          borrowedValue = price.add(borrowedValue)
        }
      }
    }

    const vaultsToProcess = useOnlyCollateralVaults
      ? infos.acceptedCollateralVaults
      : infos.suppliedVaults

    for (const vault of vaultsToProcess) {
      const vaultData = vaults.get(getAddress(vault.vault))
      if (vaultData) {
        const pricePerToken = tokenPrices.get(getAddress(vaultData.asset)) || 0n
        if (pricePerToken) {
          const price = selectPriceFromPriceApi(
            vault.assets,
            vaultData.assetDecimals,
            pricePerToken || 0n,
          )
          if (price) {
            suppliedValue = price.add(suppliedValue)
          }
        } else {
          const { address: suppliedVaultUnitOfAccount } =
            selectUnitOfAccount(vaultData)
          const price = selectPrice(
            vault.assets,
            vaultData,
            unitOfAccountPrices.get(getAddress(suppliedVaultUnitOfAccount)) ||
              0n,
          )
          if (price) {
            suppliedValue = price.add(suppliedValue)
          }
        }
      }
    }

    if (infos?.assets) {
      for (const asset of Array.from(infos?.assets.values())) {
        const vault = vaults.get(getAddress(asset.vault))
        if (vault) {
          const pricePerToken = tokenPrices.get(getAddress(vault.asset)) || 0n
          if (pricePerToken) {
            const price = selectPriceFromPriceApi(
              asset.assetsAccount,
              vault.assetDecimals,
              pricePerToken,
            )
            if (price) {
              assetsValue = price.add(assetsValue)
            }
          } else {
            const { address: assetUnitOfAccount } = selectUnitOfAccount(vault)
            const price = selectPrice(
              asset.assetsAccount,
              vault,
              unitOfAccountPrices.get(getAddress(assetUnitOfAccount)) || 0n,
            )
            if (price) {
              assetsValue = price.add(assetsValue)
            }
          }
        }
      }
    }

    return {
      ...status,
      data: status.isSuccess
        ? {
            netAssetValue: suppliedValue.subtract(borrowedValue),
            borrowedValue,
            suppliedValue,
            assetsValue,
          }
        : undefined,
    }
  },
)

/// @deprecated
export const selectNetAssetValue = createSelector(
  [
    (results: UseQueryResult<GetVaultData>[]) => results,
    selectQueryStatus,
    (_, infos?: AccountInfos) => infos,
    (_, __, unitOfAccountPrices: Map<Address, bigint>) => unitOfAccountPrices,
  ],
  (results, status, infos, unitOfAccountPrices) => {
    if (!infos) return { ...status, data: undefined }
    const vaults = new Map<Address, GetVaultData>()
    let suppliedValue = Amount.fromRawAmount(USD_TOKEN, 0)
    let borrowedValue = Amount.fromRawAmount(USD_TOKEN, 0)
    let assetsValue = Amount.fromRawAmount(USD_TOKEN, 0)
    for (const result of results) {
      if (!result.data || !isAddress(result.data.vault)) continue
      vaults.set(getAddress(result.data.vault), {
        ...result.data,
        unitOfAccount:
          result.data.unitOfAccount === zeroAddress
            ? result.data.backupAssetPriceInfo.unitOfAccount
            : result.data.unitOfAccount,
      })
    }

    const borrowVault = vaults.get(
      getAddress(infos?.borrowVault?.vault || zeroAddress),
    )

    if (borrowVault) {
      const { address: borrowVaultUnitOfAccount } =
        selectUnitOfAccount(borrowVault)

      const price = selectPrice(
        infos?.borrowVault?.borrowed || 0n,
        borrowVault,
        unitOfAccountPrices.get(getAddress(borrowVaultUnitOfAccount)) || 0n,
      )

      if (price) {
        borrowedValue = price.add(borrowedValue)
      }
    }

    for (const suppliedVault of infos.suppliedVaults) {
      const vault = vaults.get(getAddress(suppliedVault.vault))
      if (vault) {
        const { address: suppliedVaultUnitOfAccount } =
          selectUnitOfAccount(vault)
        const price = selectPrice(
          suppliedVault.assets,
          vault,
          unitOfAccountPrices.get(getAddress(suppliedVaultUnitOfAccount)) || 0n,
        )
        if (price) {
          suppliedValue = price.add(suppliedValue)
        }
      }
    }

    if (infos?.assets) {
      for (const asset of Array.from(infos?.assets.values())) {
        const vault = vaults.get(getAddress(asset.vault))
        if (vault) {
          const { address: assetUnitOfAccount } = selectUnitOfAccount(vault)
          const price = selectPrice(
            asset.assetsAccount,
            vault,
            unitOfAccountPrices.get(getAddress(assetUnitOfAccount)) || 0n,
          )
          if (price) {
            assetsValue = price.add(assetsValue)
          }
        }
      }
    }

    return {
      ...status,
      data: status.isSuccess
        ? {
            netAssetValue: suppliedValue.subtract(borrowedValue),
            borrowedValue,
            suppliedValue,
            assetsValue,
          }
        : undefined,
    }
  },
)

export const selectTotalLoanFromVaultAccountInfo = createSelector(
  [(vault?: NonNullable<GetAccountInfoData>["vaultAccountInfo"]) => vault],
  (vault) => {
    if (
      vault?.isController &&
      vault.liquidityInfo.liabilityValueBorrowing > 0n
    ) {
      return vault.liquidityInfo.liabilityValueBorrowing
    }
  },
)

export const selectTotalBorrowingPowerFromVaultAccountInfo = createSelector(
  [(vault?: NonNullable<GetAccountInfoData>["vaultAccountInfo"]) => vault],
  (vault) => {
    if (
      vault?.isController &&
      vault.liquidityInfo.liabilityValueBorrowing > 0n &&
      vault.liquidityInfo.collateralValueLiquidation > 0n
    ) {
      return vault.liquidityInfo.collateralValueLiquidation
    }
  },
)

export const selectAccountInfos = createSelector(
  [
    (results: UseQueryResult<GetAccountInfoData>[]) => results,
    (results: UseQueryResult<GetAccountInfoData>[]) =>
      selectQueryStatus(results),
  ],
  (results, status) => {
    const accountResults = new Map<Address, AccountInfos>()
    const assets = new Map<Address, { vault: Address; assetsAccount: bigint }>()
    const tokens = new Set<Address>()
    for (const result of results) {
      if (!result.data) {
        continue
      }

      tokens.add(getAddress(result.data.vaultAccountInfo.asset))

      const currentAccountData = accountResults.get(
        getAddress(result.data.vaultAccountInfo.account),
      )
      const suppliedVaults = currentAccountData?.suppliedVaults || []
      const collateralVaults = currentAccountData?.collateralVaults || []
      const rewards = currentAccountData?.rewards || []

      let borrowVault = currentAccountData?.borrowVault
      if (result.data.vaultAccountInfo.assets > 0n) {
        suppliedVaults.push(result.data.vaultAccountInfo)
      }

      if (result.data.vaultAccountInfo.isCollateral) {
        collateralVaults.push(result.data.vaultAccountInfo)
      }

      if (result.data.vaultAccountInfo.borrowed > 0n) {
        borrowVault = result.data.vaultAccountInfo
      }

      // Update suppliedVaults with collateralValue if applicable
      if (borrowVault?.liquidityInfo.collaterals?.length) {
        suppliedVaults.forEach((suppliedVault) => {
          const collateralKey = borrowVault.liquidityInfo.collaterals.findIndex(
            (collateral) => isAddressEqual(collateral, suppliedVault.vault),
          )
          const matchingCollateral =
            collateralKey !== -1
              ? borrowVault.liquidityInfo.collateralValuesLiquidation[
                  collateralKey
                ]
              : 0n

          if (matchingCollateral) {
            suppliedVault.riskAdjustedValue = matchingCollateral
          }
        })
      }

      suppliedVaults.sort(sortByRiskAdjustedValue)

      let acceptedCollateralVaults: VaultAccountInfoWithCollateralValue[] = []
      if (borrowVault && collateralVaults.length > 0) {
        const acceptedCollaterals = new Map<Address, bigint>()

        if (borrowVault.liquidityInfo.collaterals?.length) {
          for (const [
            i,
            collateral,
          ] of borrowVault.liquidityInfo.collaterals.entries() || []) {
            const collateralValue =
              borrowVault.liquidityInfo.collateralValuesLiquidation[i] || 0n
            const collateralVault = collateralVaults.find((vault) =>
              isAddressEqual(vault.vault, collateral),
            )

            if (collateralVault && collateralValue > 0n) {
              acceptedCollaterals.set(getAddress(collateral), collateralValue)
            }
          }
        }

        // Filter collateral vaults to only include those accepted by the borrow vault
        acceptedCollateralVaults =
          collateralVaults
            .filter((collateralVault) =>
              acceptedCollaterals.has(getAddress(collateralVault.vault)),
            )
            .map((collateralVault) => ({
              ...collateralVault,
              riskAdjustedValue: acceptedCollaterals.get(
                getAddress(collateralVault.vault),
              ),
            })) || []

        acceptedCollateralVaults.sort(sortByRiskAdjustedValue)
      }

      if (
        result.data.accountRewardInfo.enabledRewardsInfo.length &&
        result.data.accountRewardInfo.balanceForwarderEnabled
      ) {
        for (const reward of result.data.accountRewardInfo.enabledRewardsInfo) {
          rewards.push({
            vault: result.data.accountRewardInfo.vault,
            reward: reward.reward,
            earned: reward.earnedReward,
            account: result.data.vaultAccountInfo.account,
          })
        }
      }
      if (
        result.data.vaultAccountInfo.assetsAccount > 0n &&
        !assets.has(result.data.vaultAccountInfo.asset)
      ) {
        assets.set(result.data.vaultAccountInfo.asset, {
          vault: result.data.vaultAccountInfo.vault,
          assetsAccount: result.data.vaultAccountInfo.assetsAccount,
        })
      }

      accountResults.set(getAddress(result.data.vaultAccountInfo.account), {
        borrowVault,
        suppliedVaults,
        collateralVaults,
        acceptedCollateralVaults,
        rewards,
        assets,
        tokens: [...tokens],
        account: getAddress(result.data.vaultAccountInfo.account),
      })
    }

    return { data: accountResults, ...status }
  },
)

export const selectHasBorrowPosition = createSelector(
  selectAccountInfos,
  ({ data, ...status }) => {
    if (!data?.size) {
      return { ...status, data: undefined }
    }
    const hasBorrowPosition = Array.from(data.entries())
      .filter(([, accountInfo]) => accountInfo.borrowVault)
      .map(([account]) => getAddress(account))

    return { ...status, data: hasBorrowPosition }
  },
)

export const selectNoBorrowPosition = createSelector(
  selectAccountInfos,
  ({ data, ...status }) => {
    if (!data?.size) {
      return { ...status, data: undefined }
    }
    const hasBorrowPosition = Array.from(data.entries())
      .filter(([, accountInfo]) => !accountInfo.borrowVault)
      .map(([account]) => getAddress(account))

    return { ...status, data: hasBorrowPosition }
  },
)

export const selectNoPosition = createSelector(
  selectAccountInfos,
  ({ data, ...status }) => {
    if (!data?.size) {
      return { ...status, data: undefined }
    }
    const hasNoPosition = Array.from(data.entries())
      .filter(
        ([, accountInfo]) =>
          !accountInfo.borrowVault || accountInfo.suppliedVaults.length,
      )
      .map(([account]) => getAddress(account))

    return { ...status, data: hasNoPosition }
  },
)

export const selectNewPosition = createSelector(
  [
    (results: UseQueryResult<GetAccountInfoData>[]) =>
      selectAccountInfos(results),
    (_, primary: Address) => primary,
    (_, __, inBatchAccounts?: AccountVaults[]) => inBatchAccounts,
  ],
  ({ data, ...status }, primary, inBatchAccounts) => {
    if (!data?.size || !status.isSuccess) {
      return { ...status, data: undefined }
    }
    const indexes = Array.from(data.entries())
      .filter(
        ([, accountInfo]) =>
          accountInfo.borrowVault?.borrowed ||
          accountInfo.suppliedVaults.length,
      )
      .map(([account]) =>
        Account.getSubAccountId({ primary, subAccount: account }),
      )

    if (inBatchAccounts?.length) {
      indexes.push(
        ...inBatchAccounts.map((account) =>
          Account.getSubAccountId({ primary, subAccount: account.account }),
        ),
      )
    }
    const set = new Set(indexes.filter(Boolean))
    for (let i = 1; i <= ACCOUNT_MAX_SUB_INDEX; i++) {
      if (!set.has(i))
        return {
          ...status,
          data: Account.getSubAccount({ primary, subAccountId: i }),
        }
    }

    return { ...status, data: primary }
  },
)

export const selectAccountInfo = createSelector(
  [selectAccountInfos, (_, account: Address) => account],
  (results, account) => {
    const { data, ...status } = results
    if (data?.size) {
      const accountInfo = data.get(getAddress(account))
      if (!accountInfo) {
        return {
          isLoading: false,
          isSuccess: false,
          isPending: false,
          isFetching: false,
          data: undefined,
          isError: true,
          errors: [new AppError(CommonErrorsKey.AccountNotFound, "No account")],
        }
      }

      return { ...status, data: accountInfo }
    }

    return { ...status, data: undefined }
  },
)

export const selectAccountsInfo = createSelector(
  [selectAccountInfos, (_, accounts: Address[]) => accounts],
  (results, accounts) => {
    const { data, ...status } = results
    if (status.isSuccess && data) {
      const accountsInfo = accounts.reduce<{ [key: Address]: AccountInfos }>(
        (total, account) => ({
          ...total,
          [account]: data.get(getAddress(account)),
        }),
        {},
      )

      return { ...status, data: accountsInfo }
    }

    return { ...status, data: undefined }
  },
)

export const selectSummary = createSelector(
  [
    selectQueryStatus,
    (vaults: UseQueryResult<GetVaultData>) => vaults,
    (_, accountInfos: Map<Address, ReturnType<typeof selectAccountInfos>>) =>
      accountInfos,
    (_, __, tokenPrices: Map<Address, bigint>) => tokenPrices,
    (_, __, ___, unitOfAccountPricing: Map<Address, bigint>) =>
      unitOfAccountPricing,
    (_, __, ___, ____, useOnlyCollateralVaults?: boolean) =>
      useOnlyCollateralVaults,
  ],
  (
    status,
    results,
    accountInfos,
    tokenPrices,
    unitOfAccountPricing,
    useOnlyCollateralVaults = false,
  ) => {
    let netAssetValue = Amount.fromRawAmount(USD_TOKEN, 0n)
    let borrowedValue = Amount.fromRawAmount(USD_TOKEN, 0n)
    let suppliedValue = Amount.fromRawAmount(USD_TOKEN, 0n)
    let assetsValue = Amount.fromRawAmount(USD_TOKEN, 0n)
    if (status.isSuccess) {
      for (const accountInfo of Array.from(accountInfos.values())) {
        const prices = selectNetAssetValueV2(
          results,
          accountInfo,
          tokenPrices,
          unitOfAccountPricing,
          useOnlyCollateralVaults, // Use all supplied vaults for summary
        )
        if (prices.data) {
          netAssetValue = netAssetValue.add(prices.data.netAssetValue)
          borrowedValue = borrowedValue.add(prices.data.borrowedValue)
          suppliedValue = suppliedValue.add(prices.data.suppliedValue)
          assetsValue = prices.data.assetsValue
        }
      }
    }
    return {
      ...status,
      data: { netAssetValue, borrowedValue, suppliedValue, assetsValue },
    }
  },
)

export const selectSummaryPerAccounts = createSelector(
  [
    selectQueryStatus,
    (vaults: UseQueryResult<GetVaultData>) => vaults,
    (_, accountInfos: Map<Address, ReturnType<typeof selectAccountInfos>>) =>
      accountInfos,
    (_, __, tokenPrices: Map<Address, bigint>) => tokenPrices,
    (_, __, ___, unitOfAccountPricing: Map<Address, bigint>) =>
      unitOfAccountPricing,
    (_, __, ___, ____, useOnlyCollateralVaults?: boolean) =>
      useOnlyCollateralVaults,
  ],
  (
    status,
    results,
    accountInfos,
    tokenPrices,
    unitOfAccountPricing,
    useOnlyCollateralVaults = false,
  ) => {
    const summaryPerAccounts: Record<
      Address,
      {
        netAssetValue: Amount<Token>
        borrowedValue: Amount<Token>
        suppliedValue: Amount<Token>
      }
    > = {}
    if (status.isSuccess) {
      accountInfos?.forEach((accountInfo, account) => {
        if (!summaryPerAccounts[account]) {
          summaryPerAccounts[account] = {
            netAssetValue: Amount.fromRawAmount(USD_TOKEN, 0n),
            borrowedValue: Amount.fromRawAmount(USD_TOKEN, 0n),
            suppliedValue: Amount.fromRawAmount(USD_TOKEN, 0n),
          }
        }
        const prices = selectNetAssetValueV2(
          results,
          accountInfo,
          tokenPrices,
          unitOfAccountPricing,
          useOnlyCollateralVaults,
        )

        if (prices.data) {
          summaryPerAccounts[account].netAssetValue = summaryPerAccounts[
            account
          ].netAssetValue?.add(prices.data.netAssetValue)
          summaryPerAccounts[account].borrowedValue = summaryPerAccounts[
            account
          ].borrowedValue?.add(prices.data.borrowedValue)
          summaryPerAccounts[account].suppliedValue = summaryPerAccounts[
            account
          ].suppliedValue?.add(prices.data.suppliedValue)
        }
      })
    }
    return {
      ...status,
      data: summaryPerAccounts,
    }
  },
)

export const selectCurrentLiquidationLTV = createSelector(
  [
    (
      collateralLTV:
        | {
            collateral: `0x${string}`
            borrowLTV: bigint
            liquidationLTV: bigint
            initialLiquidationLTV: bigint
            targetTimestamp: bigint
            rampDuration: bigint
          }
        | undefined,
    ) => collateralLTV,
  ],
  (ltvInfo) => {
    if (!ltvInfo) return 0n
    const currentTimestamp = BigInt(Math.floor(Date.now() / 1000))

    if (currentTimestamp >= ltvInfo.targetTimestamp) {
      return ltvInfo.liquidationLTV
    }

    return ltvInfo.rampDuration === 0n
      ? ltvInfo.liquidationLTV
      : ltvInfo.liquidationLTV +
          ((ltvInfo.initialLiquidationLTV - ltvInfo.liquidationLTV) *
            (ltvInfo.targetTimestamp - currentTimestamp)) /
            ltvInfo.rampDuration
  },
)

/*
 * Note: The following values are in terms of unit of account:
 *
 * In VaultAccountInfo.liquidityInfo:
 * - liabilityValue
 * - collateralValueBorrowing
 * - collateralValueLiquidation
 * - collateralLiquidityBorrowingInfo[i].collateralValue
 * - collateralLiquidityLiquidationInfo[i].collateralValue
 *
 * In VaultInfoFull.liabilityPriceInfo, VaultInfoFull.collateralPriceInfo, and VaultInfoFull.backupAssetPriceInfo:
 * - amountOutMid
 * - amountOutBid
 * - amountOutAsk
 */
export const selectAccountRiskSummary = createSelector(
  [
    selectVaultsMap,
    selectQueryStatus,
    (_, infos?: AccountInfos) => infos,
    (_, __, unitOfAccountPrices: Map<Address, bigint>) => unitOfAccountPrices,
  ],
  ({ data: vaultsMap }, status, infos, unitOfAccountPrices) => {
    if (!vaultsMap) return { ...status, data: undefined }

    const collateralValuesLiquidation =
      infos?.borrowVault?.liquidityInfo.collateralValuesLiquidation || []

    const infosBorrowVault = infos?.borrowVault
    const borrowVault = vaultsMap.get(
      getAddress(infos?.borrowVault?.vault || zeroAddress),
    )
    let collateralMarketValue = 0n
    for (const [
      i,
      collateral,
    ] of infos?.borrowVault?.liquidityInfo.collaterals.entries() || []) {
      const collateralValue = collateralValuesLiquidation[i] || 0n
      const collateralLTVAccordingToBorrowVault =
        borrowVault?.collateralLTVInfo?.find((item) =>
          isAddressEqual(item.collateral, collateral),
        )
      if (!collateralLTVAccordingToBorrowVault) {
        continue
      }
      const currentLiquidationLTV = selectCurrentLiquidationLTV(
        collateralLTVAccordingToBorrowVault,
      )
      if (!currentLiquidationLTV) continue

      collateralMarketValue +=
        (collateralValue * 10_000n) / currentLiquidationLTV
    }

    const liabilityValue =
      infosBorrowVault?.liquidityInfo.liabilityValueBorrowing || 0n
    const unitOfAccountToken = new Token({
      decimals: borrowVault?.unitOfAccountDecimals.toString() || "18",
      name: borrowVault?.unitOfAccountName || "Unknown",
      address: borrowVault?.unitOfAccount || zeroAddress,
      symbol: borrowVault?.unitOfAccountSymbol || "Unknown",
    })
    const totalDebtInTermsOfUnitOfAccount = Amount.fromRawAmount(
      unitOfAccountToken,
      liabilityValue,
    )

    const totalDebtPriced = selectConvertUnitOfAccountAmountToPrice(
      liabilityValue,
      borrowVault,
      unitOfAccountPrices,
    )

    const collateralValueLiquidation =
      infosBorrowVault?.liquidityInfo.collateralValueLiquidation || 0n

    const borrowingPowerInTermsOfUnitOfAccount = Amount.fromRawAmount(
      unitOfAccountToken,
      collateralValueLiquidation,
    )

    const borrowingPowerPriced = selectConvertUnitOfAccountAmountToPrice(
      collateralValueLiquidation,
      borrowVault,
      unitOfAccountPrices,
    )

    const yourLTV =
      collateralMarketValue > 0n
        ? new Percent(liabilityValue, collateralMarketValue)
        : new Percent(0n)
    const yourLLTV =
      collateralMarketValue > 0n
        ? new Percent(collateralValueLiquidation, collateralMarketValue)
        : new Percent(0n)

    const collateralRatio =
      liabilityValue > 0n
        ? new Percent(collateralMarketValue, liabilityValue)
        : new Percent(0n)

    const health =
      collateralValueLiquidation > 0n && liabilityValue > 0n
        ? new Fraction(collateralValueLiquidation, liabilityValue)
        : new Fraction(0n)

    const percentagePrecision = 2
    const amountPrecision = 2
    const hfPrecision = 2
    const yourLTVFormattedPercentage =
      yourLTV.toPercentageString(percentagePrecision)
    const yourLLTVFormattedPercentage =
      yourLLTV.toPercentageString(percentagePrecision)
    const collateralRatioFormattedPercentage =
      collateralRatio.toPercentageString(percentagePrecision)
    const healthFormatted = health.toFixed(hfPrecision)
    const totalDebtFormatted = formatUSD(
      totalDebtPriced?.toFixed(amountPrecision) || 0,
    )
    const totalDebtNumber = +(totalDebtPriced?.toFixed(amountPrecision) || 0)
    const borrowingPowerFormatted = formatUSD(
      borrowingPowerPriced?.toFixed(amountPrecision) || 0,
    )
    const borrowingPowerNumber = +(
      borrowingPowerPriced?.toFixed(amountPrecision) || 0
    )

    const shouldFallbackLTVsToUndefined =
      borrowingPowerInTermsOfUnitOfAccount &&
      borrowingPowerInTermsOfUnitOfAccount.numerator === 0n &&
      totalDebtInTermsOfUnitOfAccount &&
      totalDebtInTermsOfUnitOfAccount.numerator === 0n

    const handleShouldFallbackUndefined = (value: string) => {
      if (shouldFallbackLTVsToUndefined) {
        return undefined
      }
      return value
    }

    return {
      ...status,
      data: {
        // Note: All *Formatted values may return undefined and should be handled with a fallback by the consumer
        // The maximum amount that can be borrowed based on the collateral as Amount<Token>
        borrowingPowerInTermsOfUnitOfAccount,
        // The borrowing power priced in USD
        borrowingPowerPriced,
        // The maximum amount that can be borrowed based on the collateral formatted in USD
        borrowingPowerFormatted: handleShouldFallbackUndefined(
          borrowingPowerFormatted,
        ),
        // The maximum amount that can be borrowed based on the collateral as a number, typically used for sliders or numerical inputs
        borrowingPowerNumber,
        // The total amount of debt owed as Amount<Token> | also known as total loan value |
        totalDebtInTermsOfUnitOfAccount,
        // The total debt priced in USD
        totalDebtPriced,
        // Total debt formatted in USD
        totalDebtFormatted: handleShouldFallbackUndefined(totalDebtFormatted),
        // Total debt as a number, typically used for sliders or numerical inputs
        totalDebtNumber,
        // Loan-to-Value ratio as a Fraction
        yourLTV,
        // Loan-to-Value ratio formatted as percentage for display purposes
        yourLTVFormattedPercentage: handleShouldFallbackUndefined(
          yourLTVFormattedPercentage,
        ),
        // Loan-to-Value ratio as a number, typically used for sliders or numerical inputs (between 0.00 and 1)
        yourLTVNumber: +yourLTV.toFixed(percentagePrecision) / 100,
        // Liquidation Loan-to-Value ratio as a Fraction
        yourLLTV,
        // Liquidation Loan-to-Value ratio formatted as percentage for display purposes
        yourLLTVFormattedPercentage: handleShouldFallbackUndefined(
          yourLLTVFormattedPercentage,
        ),
        // Liquidation Loan-to-Value ratio as a number, typically used for sliders or numerical inputs (between 0.00 and 1)
        yourLLTVNumber: +yourLLTV.toFixed(percentagePrecision) / 100,
        // Collateral ratio as a Fraction (inverse of LTV)
        collateralRatio,
        // Collateral ratio formatted as percentage for display purposes
        collateralRatioFormattedPercentage: handleShouldFallbackUndefined(
          collateralRatioFormattedPercentage,
        ),
        // Health factor as a Fraction
        health,
        // Health factor formatted as a string for display purposes
        healthFormatted,
        // Health factor as a number, typically used for sliders or numerical inputs
        healthNumber: +health.toFixed(hfPrecision),
      },
    }
  },
)

type AccountYieldsParams = {
  collaterals?: Address[]
  infos?: AccountInfos
  merklData?: Record<Address, MerklReward[]>
  nativeApys?: Record<Address, NativeApy>
  rewardsData?: any
  unitOfAccountPrices: Map<Address, bigint>
  useOnlyCollateralVaults?: boolean
  vaultsAfter?: Map<Address, VaultResults>
}

const APY_PRECISION = 10n ** 27n
const toPercent = (value: bigint | number) => new Percent(value, APY_PRECISION)
const toFraction = (value: number) =>
  new Fraction(
    (!isNaN(value) ? value : 0) * Number(APY_PRECISION),
    APY_PRECISION,
  )

export const selectAccountYields = createSelector(
  (results: UseQueryResult<GetVaultData>[]) => results,
  (_: UseQueryResult<GetVaultData>[], params: AccountYieldsParams) => params,
  (
    results,
    {
      infos,
      merklData,
      nativeApys,
      rewardsData,
      unitOfAccountPrices,
      useOnlyCollateralVaults = true,
      vaultsAfter,
    },
  ) => {
    const status = selectQueryStatus(results)
    if (!infos) return { ...status, data: undefined }
    const vaults = new Map<Address, VaultResults>()

    let totalSupplyValue = Amount.fromRawAmount(USD_TOKEN, 0n)
    let totalBorrowValue = Amount.fromRawAmount(USD_TOKEN, 0n)

    const weightedYields = {
      supply: {
        euler: Amount.fromRawAmount(USD_TOKEN, 0n),
        native: Amount.fromRawAmount(USD_TOKEN, 0n),
        rewards: Amount.fromRawAmount(USD_TOKEN, 0n),
        total: Amount.fromRawAmount(USD_TOKEN, 0n),
      },
      borrow: {
        euler: Amount.fromRawAmount(USD_TOKEN, 0n),
        native: Amount.fromRawAmount(USD_TOKEN, 0n),
        rewards: Amount.fromRawAmount(USD_TOKEN, 0n),
        total: Amount.fromRawAmount(USD_TOKEN, 0n),
      },
      net: {
        euler: Amount.fromRawAmount(USD_TOKEN, 0n),
        native: Amount.fromRawAmount(USD_TOKEN, 0n),
        rewards: Amount.fromRawAmount(USD_TOKEN, 0n),
        total: Amount.fromRawAmount(USD_TOKEN, 0n),
      },
    }

    for (const result of results) {
      if (!result.data || !isAddress(result.data.vault)) continue
      vaults.set(getAddress(result.data.vault), {
        ...result.data,
        unitOfAccount:
          result.data.unitOfAccount === zeroAddress
            ? result.data.backupAssetPriceInfo.unitOfAccount
            : result.data.unitOfAccount,
      })
    }

    if (vaultsAfter) {
      for (const [vault, vaultData] of vaultsAfter) {
        vaults.set(getAddress(vault), vaultData)
      }
    }

    const collaterals = Array.from(
      new Set(
        infos.acceptedCollateralVaults
          .filter((item) => item?.vault)
          .map((item) => getAddress(item.vault)),
      ),
    )

    for (const acceptedCollateralVault of useOnlyCollateralVaults
      ? infos.acceptedCollateralVaults
      : infos.suppliedVaults) {
      const vault = vaults.get(getAddress(acceptedCollateralVault.vault))
      if (vault) {
        const { address: suppliedVaultUnitOfAccount } =
          selectUnitOfAccount(vault)
        const vaultAssets = acceptedCollateralVault.assets

        const eulerApyPercent = toPercent(
          vault?.irmInfo.interestRateInfo[0]?.supplyAPY ?? 0n,
        )

        const nativeApyPercent = toFraction(
          nativeApys?.[getAddress(acceptedCollateralVault.asset)]?.apy ?? 0,
        )

        const rewardsApyPercent = toFraction(
          calculateTotalRewardApy(
            acceptedCollateralVault.vault,
            rewardsData,
            merklData,
          ),
        )

        const totalSupplyApy =
          +eulerApyPercent.toFixed(4) +
          +nativeApyPercent.multiply(eulerApyPercent.add(ONE)).toFixed(4) +
          +rewardsApyPercent.toFixed(4)

        const supplyValue =
          selectPrice(
            vaultAssets,
            vault,
            unitOfAccountPrices.get(getAddress(suppliedVaultUnitOfAccount)) ||
              0n,
          ) || Amount.fromRawAmount(USD_TOKEN, 0n)

        totalSupplyValue = totalSupplyValue.add(supplyValue)

        weightedYields.supply.euler = weightedYields.supply.euler.add(
          supplyValue.multiply(eulerApyPercent).multiply(100),
        )

        weightedYields.supply.native = weightedYields.supply.native.add(
          supplyValue.multiply(
            nativeApyPercent.multiply(eulerApyPercent.add(ONE)),
          ),
        )

        weightedYields.supply.rewards = weightedYields.supply.rewards.add(
          supplyValue.multiply(rewardsApyPercent),
        )
        weightedYields.supply.total = weightedYields.supply.total.add(
          supplyValue.multiply(toFraction(totalSupplyApy)),
        )
      }
    }

    const borrowVault = vaults.get(
      getAddress(infos?.borrowVault?.vault || zeroAddress),
    )

    if (borrowVault) {
      const borrowValue = selectConvertUnitOfAccountAmountToPrice(
        infos?.borrowVault?.liquidityInfo.liabilityValueBorrowing || 0n,
        borrowVault,
        unitOfAccountPrices,
      )

      if (borrowValue) {
        totalBorrowValue = borrowValue

        const borrowApys = {
          euler: +toPercent(
            borrowVault.irmInfo.interestRateInfo[0]?.borrowAPY ?? 0n,
          ).toFixed(4),
          native: +toFraction(
            nativeApys?.[getAddress(borrowVault.asset)]?.apy ?? 0,
          ).toFixed(4),
          rewards: +toFraction(
            calculateTotalBorrowRewardApy(
              borrowVault.vault,
              merklData,
              collaterals,
            ),
          ).toFixed(4),
          total: 0,
        }

        borrowApys.total =
          borrowApys.euler + borrowApys.native - borrowApys.rewards

        weightedYields.borrow.euler = borrowValue.multiply(
          toFraction(borrowApys.euler),
        )
        weightedYields.borrow.native = borrowValue.multiply(
          toFraction(borrowApys.native).multiply(
            toFraction(borrowApys.euler / 100).add(ONE),
          ),
        )
        weightedYields.borrow.rewards = borrowValue.multiply(
          toFraction(borrowApys.rewards),
        )
        weightedYields.borrow.total = borrowValue.multiply(
          toFraction(borrowApys.total),
        )
      }
    }

    weightedYields.net.euler = weightedYields.supply.euler.subtract(
      weightedYields.borrow.euler,
    )
    weightedYields.net.native = weightedYields.supply.native.subtract(
      weightedYields.borrow.native,
    )
    weightedYields.net.rewards = weightedYields.supply.rewards.add(
      weightedYields.borrow.rewards,
    )
    weightedYields.net.total = weightedYields.net.euler
      .add(weightedYields.net.native)
      .add(weightedYields.net.rewards)

    const calculateApy = (
      weightedApy: Amount<Token>,
      totalValue: Amount<Token>,
    ) =>
      totalValue.equalTo(0)
        ? 0
        : Number(
            weightedApy
              .divide(totalValue)
              .multiply(10n ** 8n)
              .toFixed(4),
          )

    const totalSupplyApys = {
      eulerApy: calculateApy(weightedYields.supply.euler, totalSupplyValue),
      nativeApy: calculateApy(weightedYields.supply.native, totalSupplyValue),
      rewardsApy: calculateApy(weightedYields.supply.rewards, totalSupplyValue),
      totalApy: calculateApy(weightedYields.supply.total, totalSupplyValue),
    }

    const totalBorrowApys = {
      eulerApy: calculateApy(
        weightedYields.borrow.euler,
        totalSupplyValue.subtract(totalBorrowValue),
      ),
      nativeApy: calculateApy(
        weightedYields.borrow.native,
        totalSupplyValue.subtract(totalBorrowValue),
      ),
      rewardsApy: calculateApy(
        weightedYields.borrow.rewards,
        totalSupplyValue.subtract(totalBorrowValue),
      ),
      totalApy: calculateApy(
        weightedYields.borrow.total,
        totalSupplyValue.subtract(totalBorrowValue),
      ),
    }

    const totalNetSupplyApys = {
      eulerApy: calculateApy(
        weightedYields.supply.euler,
        totalSupplyValue.subtract(totalBorrowValue),
      ),
      nativeApy: calculateApy(
        weightedYields.supply.native,
        totalSupplyValue.subtract(totalBorrowValue),
      ),
      rewardsApy: calculateApy(
        weightedYields.supply.rewards,
        totalSupplyValue.subtract(totalBorrowValue),
      ),
      totalApy: calculateApy(
        weightedYields.supply.total,
        totalSupplyValue.subtract(totalBorrowValue),
      ),
    }

    const totalNetApys = {
      eulerApy: calculateApy(weightedYields.net.euler, totalSupplyValue),
      nativeApy: calculateApy(weightedYields.net.native, totalSupplyValue),
      rewardsApy: calculateApy(weightedYields.net.rewards, totalSupplyValue),
      totalApy: calculateApy(weightedYields.net.total, totalSupplyValue),
    }

    const totalRoe = {
      eulerApy: calculateApy(
        weightedYields.net.euler,
        totalSupplyValue.subtract(totalBorrowValue),
      ),
      nativeApy: calculateApy(
        weightedYields.net.native,
        totalSupplyValue.subtract(totalBorrowValue),
      ),
      rewardsApy: calculateApy(
        weightedYields.net.rewards,
        totalSupplyValue.subtract(totalBorrowValue),
      ),
      totalApy: calculateApy(
        weightedYields.net.total,
        totalSupplyValue.subtract(totalBorrowValue),
      ),
    }

    return {
      ...status,
      data: status.isSuccess
        ? {
            totalSupplyApys,
            totalNetSupplyApys,
            totalSupplyValue,
            totalBorrowApys,
            totalNetApys,
            totalRoe,
          }
        : undefined,
    }
  },
)
